'use client';

import { c as _c } from "react/compiler-runtime";
import { useCoupon, useCouponId, useCouponIndices, useCouponType } from '@svs/nova-poolbetting/core/nextjs/coupon';
import type { CouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { Draw } from '@svs/nova-poolbetting/schema/draw';
import type { BettingEvent, BettingEventId, EventNumber, EventType, SportType } from '@svs/nova-poolbetting/schema/sport';
import { ClearCouponButton } from '@svs/nova-poolbetting/ui/Coupon';
import { HorizontalScrollArea } from '@svs/nova-poolbetting/ui/HorizontalScrollArea';
import { useMediaQueryAbove } from '@svs/ui/media-query/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { type ReactNode, useEffect, useRef } from 'react';
import { useSetCouponScrollbarHeight } from '@/components/index.browser';
import { CouponPixButtonClientContainer } from './CouponPixButtonClientContainer';
import { CouponRow } from './CouponRow';
type CouponRowContainerProps = {
  events: Draw['events'];
  sportType: SportType;
  bettingEvents: Record<BettingEventId, BettingEvent>;
  defaultEventType: EventType;
};

/**
 * Component responsible for rendering the outcomes for a specific event. It uses product, eventType and sportType to determine the amount of outcomes.
 *
 * @param root0 props for the component
 * @param root0.events array of events
 * @param root0.sportType sport type for the events
 * @param root0.bettingEvents record of betting events
 * @param root0.defaultEventType fallback event type
 * @returns component that renders outcomes for specific events
 */
export function CouponRowContainer(t0) {
  const $ = _c(61);
  const {
    events,
    sportType,
    bettingEvents,
    defaultEventType
  } = t0;
  const isLargeScreen = useMediaQueryAbove("md");
  const [couponId, couponIndex] = useCouponId();
  let t1;
  if ($[0] !== couponId || $[1] !== couponIndex) {
    t1 = {
      couponId,
      couponIndex
    };
    $[0] = couponId;
    $[1] = couponIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const couponType = useCouponType(t1);
  const isSingleRows = couponType === "singlerows";
  let t2;
  if ($[3] !== couponId) {
    t2 = {
      couponId
    };
    $[3] = couponId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const couponIndices = useCouponIndices(t2);
  let t3;
  if ($[5] !== couponId || $[6] !== couponIndex) {
    t3 = {
      couponId,
      couponIndex
    };
    $[5] = couponId;
    $[6] = couponIndex;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const coupon = useCoupon(t3);
  const router = useRouter();
  const searchParams = useSearchParams();
  const scrollRefElement = useRef(null);
  const renderedCouponIndicesRef = useRef(couponIndices);
  const setCouponScrollbarHeight = useSetCouponScrollbarHeight();
  let t4;
  if ($[8] !== setCouponScrollbarHeight) {
    t4 = () => {
      const scrollElement = scrollRefElement.current;
      if (!scrollElement || typeof ResizeObserver === "undefined") {
        return;
      }
      const measureScrollbarHeight = () => {
        setCouponScrollbarHeight(scrollElement.offsetHeight - scrollElement.clientHeight);
      };
      measureScrollbarHeight();
      const resizeObserver = new ResizeObserver(measureScrollbarHeight);
      resizeObserver.observe(scrollElement);
      return () => {
        resizeObserver.disconnect();
        setCouponScrollbarHeight(0);
      };
    };
    $[8] = setCouponScrollbarHeight;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isLargeScreen || $[11] !== setCouponScrollbarHeight) {
    t5 = [setCouponScrollbarHeight, isLargeScreen];
    $[10] = isLargeScreen;
    $[11] = setCouponScrollbarHeight;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[13] !== couponIndices) {
    t6 = () => {
      const renderedCouponIndices = renderedCouponIndicesRef.current;
      renderedCouponIndicesRef.current = couponIndices;
      if (couponIndices.length <= renderedCouponIndices.length) {
        return;
      }
      const addedCouponIndex = couponIndices.find(index => !renderedCouponIndices.includes(index));
      const scrollElement_0 = scrollRefElement.current;
      if (addedCouponIndex === undefined || !scrollElement_0) {
        return;
      }
      const addedCouponElement = scrollElement_0.querySelector(`[data-coupon-index="${addedCouponIndex}"]`);
      if (!addedCouponElement) {
        return;
      }
      scrollElement_0.scrollTo({
        left: scrollElement_0.scrollLeft + addedCouponElement.getBoundingClientRect().right - scrollElement_0.getBoundingClientRect().right,
        behavior: "smooth"
      });
    };
    t7 = [couponIndices];
    $[13] = couponIndices;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[16] !== coupon || $[17] !== couponIndex || $[18] !== couponIndices || $[19] !== router || $[20] !== searchParams) {
    t8 = () => {
      if (!coupon || !couponIndices.includes(couponIndex)) {
        const qs = new URLSearchParams(searchParams.toString());
        if (couponIndices.includes(couponIndex - 1 as CouponIndex)) {
          qs.set("coupon", JSON.stringify(couponIndex - 1));
        } else {
          qs.delete("coupon");
        }
        setTimeout(() => {
          router.push(`?${qs.toString()}`);
        }, 0);
      }
    };
    t9 = [coupon, couponIndex, couponIndices, router, searchParams];
    $[16] = coupon;
    $[17] = couponIndex;
    $[18] = couponIndices;
    $[19] = router;
    $[20] = searchParams;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useEffect(t8, t9);
  if (!coupon) {
    return;
  }
  let t10;
  if ($[23] !== couponIndex || $[24] !== couponIndices || $[25] !== isLargeScreen) {
    t10 = isLargeScreen ? couponIndices : [couponIndex];
    $[23] = couponIndex;
    $[24] = couponIndices;
    $[25] = isLargeScreen;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const couponIndicesToRender = t10;
  const hasMultipleCoupons = couponIndices.length > 1;
  let t11;
  if ($[27] !== isLargeScreen || $[28] !== router || $[29] !== searchParams) {
    t11 = t12 => {
      const {
        eventNumber
      } = t12;
      const qs_0 = new URLSearchParams(searchParams.toString());
      qs_0.set("event", eventNumber.toString());
      if (!isLargeScreen) {
        qs_0.set("modal", "stats");
      }
      router.push(`?${qs_0.toString()}`, {
        scroll: false
      });
    };
    $[27] = isLargeScreen;
    $[28] = router;
    $[29] = searchParams;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const handleClick = t11;
  let t12;
  if ($[31] !== bettingEvents || $[32] !== coupon || $[33] !== couponIndex || $[34] !== couponIndicesToRender || $[35] !== defaultEventType || $[36] !== events || $[37] !== handleClick || $[38] !== hasMultipleCoupons || $[39] !== isSingleRows || $[40] !== sportType) {
    let t13;
    if ($[42] !== bettingEvents || $[43] !== coupon || $[44] !== couponIndex || $[45] !== couponIndicesToRender.length || $[46] !== defaultEventType || $[47] !== events || $[48] !== handleClick || $[49] !== hasMultipleCoupons || $[50] !== isSingleRows || $[51] !== sportType) {
      t13 = couponIndexToRender => <CouponRow key={couponIndexToRender} couponIndex={couponIndexToRender} events={events} sportType={sportType} bettingEvents={bettingEvents} defaultEventType={defaultEventType} hasMultipleCoupons={hasMultipleCoupons} selectedCouponIndex={couponIndex} handleClick={handleClick} drawNumber={coupon.drawNumber} couponRowFooter={couponIndicesToRender.length > 1 && isSingleRows ? <div data-coupon-row-footer={true} className={`${couponIndexToRender === couponIndex ? "lg:bg-secondary-variant" : ""} bg-surface-elevated border-stroke-low flex max-h-15 min-h-15 items-center justify-between border-t border-l p-2`}><CouponPixButtonClientContainer couponIndex={couponIndexToRender} /><ClearCouponButton couponIndex={couponIndexToRender} /></div> : undefined} />;
      $[42] = bettingEvents;
      $[43] = coupon;
      $[44] = couponIndex;
      $[45] = couponIndicesToRender.length;
      $[46] = defaultEventType;
      $[47] = events;
      $[48] = handleClick;
      $[49] = hasMultipleCoupons;
      $[50] = isSingleRows;
      $[51] = sportType;
      $[52] = t13;
    } else {
      t13 = $[52];
    }
    t12 = couponIndicesToRender.map(t13);
    $[31] = bettingEvents;
    $[32] = coupon;
    $[33] = couponIndex;
    $[34] = couponIndicesToRender;
    $[35] = defaultEventType;
    $[36] = events;
    $[37] = handleClick;
    $[38] = hasMultipleCoupons;
    $[39] = isSingleRows;
    $[40] = sportType;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[53] !== t12) {
    t13 = <div ref={scrollRefElement} className="flex h-full min-w-40 items-stretch lg:min-w-65.5 lg:overflow-x-auto">{t12}</div>;
    $[53] = t12;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  const couponRows = t13;
  let t14;
  if ($[55] !== couponRows || $[56] !== isLargeScreen) {
    t14 = isLargeScreen && <HorizontalScrollArea scrollElementRef={scrollRefElement} leftLabel={"Skrolla v\xE4nster"} rightLabel={"Skrolla h\xF6ger"}>{couponRows}</HorizontalScrollArea>;
    $[55] = couponRows;
    $[56] = isLargeScreen;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  const t15 = !isLargeScreen && couponRows;
  let t16;
  if ($[58] !== t14 || $[59] !== t15) {
    t16 = <div className="relative isolate h-full self-stretch lg:overflow-x-auto">{t14}{t15}</div>;
    $[58] = t14;
    $[59] = t15;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  return t16;
}